'use client';

import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

const AVAILABLE_LANGUAGES = ['en', 'fr', 'es', 'de'];
const FALLBACK_LANGUAGE = 'en';

const crowdinProjectId = Number(process.env.NEXT_PUBLIC_CROWDIN_PROJECT_ID);
const crowdinToken = process.env.NEXT_PUBLIC_CROWDIN_TOKEN ?? '';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let cachedTranslations: Record<string, any> | null = null;

const deepMerge = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  target: Record<string, any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  source: Record<string, any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, any> => {
  const output = { ...target };
  for (const key of Object.keys(source)) {
    if (
      source[key] &&
      typeof source[key] === 'object' &&
      !Array.isArray(source[key]) &&
      target[key] &&
      typeof target[key] === 'object' &&
      !Array.isArray(target[key])
    ) {
      output[key] = deepMerge(target[key], source[key]);
    } else {
      output[key] = source[key];
    }
  }
  return output;
};

const fetchAllTranslations = async () => {
  if (cachedTranslations) return cachedTranslations;

  const { default: Crowdin } = await import('@crowdin/crowdin-api-client');
  const { translationsApi, sourceFilesApi } = new Crowdin({
    token: crowdinToken,
  });

  const filesResponse = await sourceFilesApi.listProjectFiles(crowdinProjectId);
  const activeFiles = filesResponse.data.filter(
    (file) => file.data.status === 'active'
  );

  if (activeFiles.length === 0) {
    throw new Error('No active translation files found');
  }

  const translations = await Promise.all(
    AVAILABLE_LANGUAGES.map(async (language) => {
      const fileTranslations = await Promise.all(
        activeFiles.map(async (file) => {
          const downloadLink = await (language === 'en'
            ? sourceFilesApi.downloadFile(crowdinProjectId, file.data.id)
            : translationsApi.buildProjectFileTranslation(
              crowdinProjectId,
              file.data.id,
              { targetLanguageId: language === 'es' ? 'es-ES' : language }
            ));
          const response = await fetch(downloadLink.data.url);
          return response.json();
        })
      );
      return {
        language,
        translation: fileTranslations.reduce(
          (acc, current) => deepMerge(acc, current),
          {}
        ),
      };
    })
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cachedTranslations = translations.reduce<Record<string, any>>(
    (acc, { language, translation }) => {
      acc[language] = translation;
      return acc;
    },
    {}
  );

  return cachedTranslations;
};

const i18n = i18next.createInstance();

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: FALLBACK_LANGUAGE,
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false,
    },
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      request: async (_options: any, url: any, _payload: any, callback: any) => {
        const lng = url.split('/')[2];
        try {
          const allTranslations = await fetchAllTranslations();
          if (allTranslations && allTranslations[lng]) {
            callback(null, {
              status: 200,
              data: JSON.stringify(allTranslations[lng]),
            });
          } else {
            callback(new Error(`Translation for ${lng} not found`), {
              status: 404,
              data: null,
            });
          }
        } catch (e) {
          console.error('Error fetching translations:', e);
          callback(e, { status: 500, data: null });
        }
      },
    },
  });

i18n.on('languageChanged', (lng) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('i18nextLng', lng);
  }
});

export default i18n;
